import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {hoverSelector} from '../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {VisuallyHidden} from '../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {makeSizeConstants} from '../../../../libs/shared/ui/src/styles/styleHelpers';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {UserTheme, useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {TeammateModel} from '../../../models/api/teammateModel';
import {AvailabilityStatusTypesEnum} from '../../../store/data/teammates/availabilityStatusSelectors';
import {TeammateOnlineStatusesEnum} from '../../../store/data/teammates/onlineTeammatesSelectors';
import {AvatarProps, AvatarStyleProps} from './avatar';

/*
 * Props.
 */

export interface AvatarBadgeStatusProps extends Omit<AvatarProps, 'size'> {
  /** The teammate to display. */
  teammate: TeammateModel;
  /** Whether the teammate is online. */
  onlineStatus?: TeammateOnlineStatusesEnum;
  /** Overwrite the automated color. */
  color?: string;
  size: VisualSizesEnum;
  availabilityStatus?: AvailabilityStatusTypesEnum;
}

/*
 * Style.
 */

const badgeSizes = makeSizeConstants(5, 6, 9);
const borderSizes = makeSizeConstants(1, 1.5, 2.5);

interface BadgeStyleProps extends Omit<AvatarStyleProps, '$size'> {
  $color: string;
  $size: VisualSizesEnum;
}

const StyledBadgeDiv = styled.div<BadgeStyleProps>`
  position: absolute;
  right: 2.5%;
  bottom: 0;

  width: ${(p) => badgeSizes[p.$size]}px;
  height: ${(p) => badgeSizes[p.$size]}px;

  background-color: ${(p) => p.$color};
  border-radius: 50%;

  ${(p) => maybeAddBorderStyle(p)};
  ${(p) => maybeAddBorderHoverStyle(p)};
`;

function maybeAddBorderStyle(props: BadgeStyleProps) {
  if (!props.$borderColor) {
    return '';
  }

  return css`
    ${(p) => boxShadow(p.theme, `0 0 0 ${borderSizes[props.$size]}px ${props.$borderColor}`)};
  `;
}

function maybeAddBorderHoverStyle(props: BadgeStyleProps) {
  if (!props.$borderHoverColor) {
    return '';
  }

  return css`
    ${hoverSelector} {
      ${(p) => boxShadow(p.theme, `0 0 0 ${borderSizes[props.$size]}px ${props.$borderHoverColor}`)};
    }
  `;
}

/*
 * Component.
 */

export const AvatarBadgeStatus: FC<AvatarBadgeStatusProps> = (props) => {
  const theme = useUserTheme();
  const styleProps: BadgeStyleProps = {
    $size: props.size,
    $borderColor: props.borderColor,
    $borderHoverColor: props.borderHoverColor,
    $color: computeTeammateStatusColor(props, theme),
  };

  return (
    <>
      <StyledBadgeDiv {...styleProps} className={props.className} />
      <VisuallyHidden>{renderTeammateStatusLabel(props)}</VisuallyHidden>
    </>
  );
};

function renderTeammateStatusLabel(props: AvatarBadgeStatusProps) {
  const {teammate, onlineStatus, availabilityStatus} = props;

  if (!teammate.isAvailable) {
    return <Trans id="avatarbadgestatus-ooo">Out of office</Trans>;
  }

  if (
    availabilityStatus === AvailabilityStatusTypesEnum.DO_NOT_DISTURB ||
    availabilityStatus === AvailabilityStatusTypesEnum.MUTED
  ) {
    return <Trans id="avatarbadgestatus-do-not-disturb">Do not disturb</Trans>;
  }

  if (
    onlineStatus !== TeammateOnlineStatusesEnum.ONLINE &&
    availabilityStatus !== AvailabilityStatusTypesEnum.DEFAULT
  ) {
    return <Trans id="avatarbadgestatus-away">Away</Trans>;
  }

  return <Trans id="avatarbadgestatus-available">Available</Trans>;
}

/*
 * Helpers.
 */

function computeTeammateStatusColor(props: AvatarBadgeStatusProps, theme: UserTheme) {
  // If a color was specified, use it.
  const {teammate, onlineStatus, color} = props;
  if (color) {
    return color;
  }

  // Otherwise, compute the color from the teammate.
  if (!teammate.isAvailable) {
    return theme.palette.red.shade40;
  }

  if (onlineStatus !== TeammateOnlineStatusesEnum.ONLINE) {
    return theme.palette.orange.shade40;
  }

  return theme.palette.green.shade40;
}
